import { isUnknownRecord } from "@shared/validation/narrow";
import type { ColumnAlignment, ColumnMeta } from "../column-types";

const COLUMN_ALIGNMENTS: readonly ColumnAlignment[] = ["left", "center", "right"];

type PresentationMeta = Pick<
  ColumnMeta,
  | "align"
  | "headerClassName"
  | "cellClassName"
  | "mobileHidden"
  | "mobilePrimary"
  | "mobileRow"
>;

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function optionalBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function asColumnMeta(meta: unknown): PresentationMeta | undefined {
  if (!isUnknownRecord(meta)) return undefined;
  return {
    align: COLUMN_ALIGNMENTS.find((alignment) => alignment === meta.align),
    headerClassName: optionalString(meta.headerClassName),
    cellClassName: optionalString(meta.cellClassName),
    mobileHidden: optionalBoolean(meta.mobileHidden),
    mobilePrimary: optionalBoolean(meta.mobilePrimary),
    mobileRow: meta.mobileRow === "secondary" || meta.mobileRow === "trailing" ? meta.mobileRow : undefined,
  };
}

function resolveAlignment(meta: unknown): ColumnAlignment | undefined {
  const columnMeta = asColumnMeta(meta);
  if (columnMeta?.align) return columnMeta.align;
  const classes = `${columnMeta?.cellClassName ?? ""} ${columnMeta?.headerClassName ?? ""}`;
  if (/(?:^|\s)text-right(?:\s|$)/.test(classes)) return "right";
  if (/(?:^|\s)text-center(?:\s|$)/.test(classes)) return "center";
  if (/(?:^|\s)text-left(?:\s|$)/.test(classes)) return "left";
  return undefined;
}

const HEADER_ALIGNMENT: Record<ColumnAlignment, string> = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
};

const CELL_ALIGNMENT: Record<ColumnAlignment, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export function headerAlignmentClass(meta: unknown): string | undefined {
  const align = resolveAlignment(meta);
  return align ? HEADER_ALIGNMENT[align] : undefined;
}

export function headerClassName(meta: unknown): string | undefined {
  return asColumnMeta(meta)?.headerClassName;
}

export function cellAlignmentClass(meta: unknown): string | undefined {
  const align = resolveAlignment(meta);
  return align ? CELL_ALIGNMENT[align] : undefined;
}

export function cellClassName(meta: unknown): string | undefined {
  return asColumnMeta(meta)?.cellClassName;
}

export function isMobileHidden(meta: unknown): boolean {
  return asColumnMeta(meta)?.mobileHidden === true;
}

export function isMobilePrimary(meta: unknown): boolean {
  return asColumnMeta(meta)?.mobilePrimary === true;
}

export function mobileRowSlot(meta: unknown): "secondary" | "trailing" | undefined {
  return asColumnMeta(meta)?.mobileRow;
}
